import { useState, useEffect } from "react";
import { getProviderLogo } from "../services/providerService";

const LOGO_CONCURRENCY = 4;

/**
 * Custom hook to fetch and manage provider logo
 * @param {string} providerId - The provider ID to fetch logo for
 * @param {number} version - Bump it after the logo changes to fetch it again
 * @returns {string|null} - Blob URL of the logo or null
 */
export const useProviderLogo = (providerId, version = 0) => {
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    if (!providerId) return;

    let currentLogoUrl = null;

    const fetchLogo = async () => {
      const url = await getProviderLogo(providerId);
      if (url) {
        currentLogoUrl = url;
        setLogoUrl(url);
      }
    };

    fetchLogo();

    // Cleanup blob URL on unmount
    return () => {
      if (currentLogoUrl) {
        URL.revokeObjectURL(currentLogoUrl);
      }
    };
  }, [providerId, version]);

  return logoUrl;
};

/**
 * Custom hook to fetch and manage multiple provider logos
 * @param {Array<{id: string}>} providers - Array of providers with id field
 * @returns {Object} - Map of provider_id to blob URL
 */
export const useProviderLogos = (providers) => {
  const [logoMap, setLogoMap] = useState({});

  useEffect(() => {
    if (!providers || providers.length === 0) return;

    let currentLogoMap = {};
    let cancelled = false;

    const fetchLogos = async () => {
      // A few at a time: every logo request holds a backend database connection, and
      // firing all of them at once starved the page's other requests until they timed out.
      const queue = [...providers];
      const worker = async () => {
        while (queue.length && !cancelled) {
          const provider = queue.shift();
          const logoUrl = await getProviderLogo(provider.id);
          if (!logoUrl) continue;
          if (cancelled) {
            URL.revokeObjectURL(logoUrl);
            return;
          }
          currentLogoMap = { ...currentLogoMap, [provider.id]: logoUrl };
          setLogoMap(currentLogoMap); // show each logo as it arrives
        }
      };
      await Promise.all(Array.from({ length: Math.min(LOGO_CONCURRENCY, queue.length) }, worker));
    };

    fetchLogos();

    // Cleanup blob URLs on unmount
    return () => {
      cancelled = true;
      Object.values(currentLogoMap).forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers]);

  return logoMap;
};
